import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Users, Plus, QrCode, Link, Download, Eye, EyeOff, Edit, Trash2, Copy, CheckCircle, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, Invitation } from '../../services/dataService';
import { usePermissions, PERMISSIONS, DEFAULT_PERMISSIONS } from '../../hooks/usePermissions';
import QrCodeLib from 'qrcode';
import { saveAs } from 'file-saver';
import { getPublicUrl } from '../../utils/domainUtils';

interface TenantUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  permissions?: string[];
  invitedBy?: string;
  joinedVia?: 'signup' | 'invite' | 'qr';
  createdAt: Date;
  updatedAt: Date;
}

type TabType = 'users' | 'invitations';

const UserManagement: React.FC = () => {
  const { tenantData, currentUser } = useAuth();
  const { hasPermission, PERMISSIONS: PERMS } = usePermissions();
  const [activeTab, setActiveTab] = useState<TabType>('users');
  
  // Users state
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  
  // Invitations state
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  
  // UI state
  const [showCreateInvitation, setShowCreateInvitation] = useState(false);
  const [createInvitationLoading, setCreateInvitationLoading] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [invitationUrl, setInvitationUrl] = useState<string>('');
  const [copied, setCopied] = useState(false);
  
  // Edit user state
  const [showEditUser, setShowEditUser] = useState(false);
  const [editingUser, setEditingUser] = useState<TenantUser | null>(null);
  const [editUserLoading, setEditUserLoading] = useState(false);
  const [editUserPermissions, setEditUserPermissions] = useState<string[]>([]);
  
  // Delete user state
  const [deletingUser, setDeletingUser] = useState<TenantUser | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);
  
  // Form state
  const [workerName, setWorkerName] = useState('');
  const [workerEmail, setWorkerEmail] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(DEFAULT_PERMISSIONS.worker);
  const [expirationDays, setExpirationDays] = useState(7);

  // Permission categories for better UX
  const permissionCategories: Record<string, { permissions: string[]; descriptions: Record<string, string> }> = {
    'Dashboard': {
      permissions: [PERMS.DASHBOARD_FULL, PERMS.DASHBOARD_LIMITED],
      descriptions: {
        [PERMS.DASHBOARD_FULL]: 'Full dashboard access with all metrics',
        [PERMS.DASHBOARD_LIMITED]: 'Limited dashboard view with basic metrics'
      }
    },
    'Orders': {
      permissions: [PERMS.ORDERS_FULL, PERMS.ORDERS_VIEW, PERMS.ORDERS_UPDATE],
      descriptions: {
        [PERMS.ORDERS_FULL]: 'Create, view, update, and delete orders',
        [PERMS.ORDERS_VIEW]: 'View orders only',
        [PERMS.ORDERS_UPDATE]: 'View and update order status'
      }
    },
    'Inventory': {
      permissions: [PERMS.INVENTORY_FULL, PERMS.INVENTORY_VIEW],
      descriptions: {
        [PERMS.INVENTORY_FULL]: 'Full inventory management',
        [PERMS.INVENTORY_VIEW]: 'View inventory only'
      }
    },
    'Customers': {
      permissions: [PERMS.CUSTOMERS_FULL, PERMS.CUSTOMERS_VIEW],
      descriptions: {
        [PERMS.CUSTOMERS_FULL]: 'Full customer management',
        [PERMS.CUSTOMERS_VIEW]: 'View customers only'
      }
    },
    'Payments': {
      permissions: [PERMS.PAYMENTS_FULL],
      descriptions: {
        [PERMS.PAYMENTS_FULL]: 'Process and manage payments'
      }
    },
    'History': {
      permissions: [PERMS.HISTORY_FULL, PERMS.HISTORY_LIMITED],
      descriptions: {
        [PERMS.HISTORY_FULL]: 'Full order history access',
        [PERMS.HISTORY_LIMITED]: 'Limited order history view'
      }
    }
  };

  const loadUsers = useCallback(async () => {
    if (!tenantData?.id) return;
    
    try {
      setUsersLoading(true);
      const tenantUsers = await dataService.getTenantUsers(tenantData.id);
      setUsers(tenantUsers as TenantUser[]);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setUsersLoading(false);
    }
  }, [tenantData]);

  const loadInvitations = useCallback(async () => {
    if (!tenantData?.id) return;
    
    try {
      setInvitationsLoading(true);
      const tenantInvitations = await dataService.getInvitations(tenantData.id);
      setInvitations(tenantInvitations);
    } catch (error) {
      console.error('Error loading invitations:', error);
    } finally {
      setInvitationsLoading(false);
    }
  }, [tenantData]);

  useEffect(() => {
    if (tenantData?.id) {
      loadUsers();
      loadInvitations();
    }
  }, [tenantData, loadUsers, loadInvitations]);

  const handleCreateInvitation = async () => {
    if (!tenantData?.id || !currentUser?.uid) return;
    
    try {
      setCreateInvitationLoading(true);
      
      const invitationId = await dataService.createInvitation(
        tenantData.id,
        currentUser.uid,
        selectedPermissions,
        expirationDays,
        workerName || undefined,
        workerEmail || undefined
      );

      // Get the created invitation to get the token
      const invitation = await dataService.getById<Invitation>(tenantData.id, 'invitations', invitationId);
      
      if (invitation) {
        // Generate invitation URL
        const url = getPublicUrl(`invite/${tenantData.id}/${invitation.token}`);
        setInvitationUrl(url);
        
        // Generate QR code
        const qrCodeData = await QrCodeLib.toDataURL(url, {
          width: 300,
          margin: 2,
          color: {
            dark: '#1f2937',
            light: '#ffffff'
          }
        });
        setQrCodeDataUrl(qrCodeData);
        
        // Reset form
        setWorkerName('');
        setWorkerEmail('');
        setSelectedPermissions(DEFAULT_PERMISSIONS.worker);
        setExpirationDays(7);
        
        // Reload invitations
        await loadInvitations();
      }
    } catch (error) {
      console.error('Error creating invitation:', error);
      alert('Failed to create invitation. Please try again.');
    } finally {
      setCreateInvitationLoading(false);
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    if (!tenantData?.id) return;
    
    if (window.confirm('Are you sure you want to revoke this invitation?')) {
      try {
        await dataService.revokeInvitation(tenantData.id, invitationId);
        await loadInvitations();
      } catch (error) {
        console.error('Error revoking invitation:', error);
        alert('Failed to revoke invitation. Please try again.');
      }
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const handleEditUser = (user: TenantUser) => {
    setEditingUser(user);
    setEditUserPermissions(user.permissions || []);
    setShowEditUser(true);
  };

  const handleSaveUserEdit = async () => {
    if (!editingUser || !tenantData?.id) return;
    
    try {
      setEditUserLoading(true);
      await dataService.updateUserPermissions(tenantData.id, editingUser.id, editUserPermissions);
      await loadUsers(); // Reload users to get updated data
      setShowEditUser(false);
      setEditingUser(null);
      setEditUserPermissions([]);
    } catch (error) {
      console.error('Error updating user permissions:', error);
      alert('Failed to update user permissions. Please try again.');
    } finally {
      setEditUserLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setShowEditUser(false);
    setEditingUser(null);
    setEditUserPermissions([]);
  };

  const handleDeleteUser = (user: TenantUser) => {
    setDeletingUser(user);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingUser || !tenantData?.id) return;
    
    try {
      setDeleteUserLoading(true);
      await dataService.removeUserFromTenant(tenantData.id, deletingUser.id);
      await loadUsers(); // Reload users to get updated data
      setIsDeleteModalOpen(false);
      setDeletingUser(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user. Please try again.');
    } finally {
      setDeleteUserLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setDeletingUser(null);
  };

  const downloadQRCode = () => {
    if (qrCodeDataUrl) {
      fetch(qrCodeDataUrl)
        .then(res => res.blob())
        .then(blob => {
          saveAs(blob, `${tenantData?.businessName || 'worker'}-invitation-qr.png`);
        });
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString();
  };

  const getInvitationStatus = (invitation: Invitation) => {
    if (invitation.status === 'used') return 'Used';
    if (invitation.status === 'expired') return 'Expired';
    if (new Date(invitation.expiresAt) < new Date()) return 'Expired';
    return 'Active';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
      case 'Used': return 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300';
      case 'Expired': return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  // Check if user has permission to access this page
  if (!hasPermission(PERMS.USER_MANAGEMENT)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md w-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg shadow-md p-6 text-center animate-fade-in-up">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <X className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Access Denied</h3>
          <p className="text-gray-600 dark:text-gray-300">You don't have permission to manage users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto animate-page-enter">
      <div className="mb-4 sm:mb-6 animate-fade-in-up">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">User Management</h1>
        <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">Manage workers and team access</p>
      </div>

      {/* Tabs */}
      <div className="mb-4 sm:mb-6 animate-fade-in-up animation-delay-200">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-4 sm:space-x-8">
            <button
              onClick={() => setActiveTab('users')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === 'users'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Team Members ({users.length})
            </button>
            <button
              onClick={() => setActiveTab('invitations')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === 'invitations'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <Link className="w-4 h-4 inline mr-2" />
              Invitations ({invitations.length})
            </button>
          </nav>
        </div>
      </div>

      {activeTab === 'users' && (
        <div className="animate-fade-in-up animation-delay-300">
          {/* Users List */}
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md shadow rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Team Members</h3>
            </div>
            
            {usersLoading ? (
              <div className="p-4 sm:p-6 text-center text-gray-600 dark:text-gray-400">Loading users...</div>
            ) : users.length === 0 ? (
              <div className="p-4 sm:p-6 text-center text-gray-500 dark:text-gray-400">No users found</div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Permissions
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Joined
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white/50 dark:bg-gray-800/50 divide-y divide-gray-200 dark:divide-gray-700">
                      {users.map((user) => (
                        <tr key={user.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {user.firstName} {user.lastName}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              user.role === 'owner' 
                                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300' 
                                : 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300'
                            }`}>
                              {user.role}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 dark:text-white">
                              {user.role === 'owner' ? (
                                <span className="text-purple-600 dark:text-purple-400">All Permissions</span>
                              ) : (
                                <div className="flex flex-wrap gap-1">
                                  {(user.permissions || []).slice(0, 3).map((permission) => (
                                    <span key={permission} className="inline-flex px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded">
                                      {permission.replace('_', ' ').toLowerCase()}
                                    </span>
                                  ))}
                                  {(user.permissions || []).length > 3 && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      +{(user.permissions || []).length - 3} more
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {formatDate(user.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {user.role !== 'owner' && (
                              <>
                                <button 
                                  onClick={() => handleEditUser(user)}
                                  className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 mr-3 transition-colors duration-200"
                                  title="Edit user permissions"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button 
                                  onClick={() => handleDeleteUser(user)}
                                  className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors duration-200"
                                  title="Delete user"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden p-3 sm:p-4 space-y-3 sm:space-y-4">
                  {users.map((user, index) => (
                    <div 
                      key={user.id} 
                      className="bg-white/50 dark:bg-gray-700/50 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-600 animate-card-enter"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      {/* User Info */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                            {user.firstName} {user.lastName}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                        </div>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.role === 'owner' 
                            ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300' 
                            : 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300'
                        }`}>
                          {user.role}
                        </span>
                      </div>

                      {/* Permissions */}
                      <div className="mb-3">
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Permissions:</p>
                        <div className="text-sm text-gray-900 dark:text-white">
                          {user.role === 'owner' ? (
                            <span className="text-purple-600 dark:text-purple-400 text-xs">All Permissions</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {(user.permissions || []).slice(0, 2).map((permission) => (
                                <span key={permission} className="inline-flex px-2 py-1 text-xs bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded">
                                  {permission.replace('_', ' ').toLowerCase()}
                                </span>
                              ))}
                              {(user.permissions || []).length > 2 && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  +{(user.permissions || []).length - 2} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Joined Date & Actions */}
                      <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-600">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Joined: {formatDate(user.createdAt)}
                        </p>
                        {user.role !== 'owner' && (
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => handleEditUser(user)}
                              className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 transition-colors duration-200 p-1"
                              title="Edit user permissions"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleDeleteUser(user)}
                              className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors duration-200 p-1"
                              title="Delete user"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === 'invitations' && (
        <div className="animate-fade-in-up animation-delay-300">
          {/* Create Invitation Button */}
          <div className="mb-4 sm:mb-6">
            <button
              onClick={() => setShowCreateInvitation(true)}
              className="bg-primary-600 dark:bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 flex items-center transition-colors duration-200 animate-button-hover w-full sm:w-auto justify-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Invitation
            </button>
          </div>

          {/* Invitations List */}
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md shadow rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Active Invitations</h3>
            </div>
            
            {invitationsLoading ? (
              <div className="p-4 sm:p-6 text-center text-gray-600 dark:text-gray-400">Loading invitations...</div>
            ) : invitations.length === 0 ? (
              <div className="p-4 sm:p-6 text-center text-gray-500 dark:text-gray-400">No invitations found</div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Worker Details
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Permissions
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Expires
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white/50 dark:bg-gray-800/50 divide-y divide-gray-200 dark:divide-gray-700">
                      {invitations.map((invitation) => (
                        <tr key={invitation.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {invitation.workerName || 'Unnamed Worker'}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">{invitation.workerEmail || 'No email'}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(getInvitationStatus(invitation))}`}>
                              {getInvitationStatus(invitation)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1">
                              {invitation.assignedFeatures.slice(0, 2).map((feature) => (
                                <span key={feature} className="inline-flex px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded">
                                  {feature.replace('_', ' ').toLowerCase()}
                                </span>
                              ))}
                              {invitation.assignedFeatures.length > 2 && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  +{invitation.assignedFeatures.length - 2} more
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {formatDate(invitation.expiresAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {getInvitationStatus(invitation) === 'Active' && (
                              <>
                                <button
                                  onClick={() => copyToClipboard(getPublicUrl(`invite/${tenantData?.id}/${invitation.token}`))}
                                  className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 mr-3 transition-colors duration-200"
                                  title="Copy invitation link"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleRevokeInvitation(invitation.id!)}
                                  className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors duration-200"
                                  title="Revoke invitation"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden p-3 sm:p-4 space-y-3 sm:space-y-4">
                  {invitations.map((invitation, index) => (
                    <div 
                      key={invitation.id} 
                      className="bg-white/50 dark:bg-gray-700/50 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-600 animate-card-enter"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      {/* Worker Info */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                            {invitation.workerName || 'Unnamed Worker'}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{invitation.workerEmail || 'No email'}</p>
                        </div>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(getInvitationStatus(invitation))}`}>
                          {getInvitationStatus(invitation)}
                        </span>
                      </div>

                      {/* Permissions */}
                      <div className="mb-3">
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Permissions:</p>
                        <div className="flex flex-wrap gap-1">
                          {invitation.assignedFeatures.slice(0, 2).map((feature) => (
                            <span key={feature} className="inline-flex px-2 py-1 text-xs bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded">
                              {feature.replace('_', ' ').toLowerCase()}
                            </span>
                          ))}
                          {invitation.assignedFeatures.length > 2 && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              +{invitation.assignedFeatures.length - 2} more
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Expires & Actions */}
                      <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-600">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Expires: {formatDate(invitation.expiresAt)}
                        </p>
                        {getInvitationStatus(invitation) === 'Active' && (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => copyToClipboard(getPublicUrl(`invite/${tenantData?.id}/${invitation.token}`))}
                              className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 transition-colors duration-200 p-1"
                              title="Copy invitation link"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRevokeInvitation(invitation.id!)}
                              className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors duration-200 p-1"
                              title="Revoke invitation"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create Invitation Modal */}
      {showCreateInvitation && createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 animate-fade-in" style={{ zIndex: 999999 }}>
          <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-lg max-w-2xl w-full h-full md:h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700 shadow-2xl">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Create Worker Invitation</h2>
                <button
                  onClick={() => setShowCreateInvitation(false)}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {!invitationUrl ? (
                <div className="space-y-6">
                  {/* Worker Details */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Worker Details (Optional)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Worker Name
                        </label>
                        <input
                          type="text"
                          value={workerName}
                          onChange={(e) => setWorkerName(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white/90 dark:bg-gray-800/90 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          placeholder="Enter worker's name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Worker Email
                        </label>
                        <input
                          type="email"
                          value={workerEmail}
                          onChange={(e) => setWorkerEmail(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white/90 dark:bg-gray-800/90 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          placeholder="Enter worker's email"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Permissions */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Assign Permissions</h3>
                    <div className="space-y-4">
                      {Object.entries(permissionCategories).map(([category, { permissions, descriptions }]) => (
                        <div key={category} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50/50 dark:bg-gray-700/30">
                          <h4 className="font-medium text-gray-900 dark:text-white mb-3">{category}</h4>
                          <div className="space-y-2">
                            {permissions.map((permission) => (
                              <label key={permission} className="flex items-start">
                                <input
                                  type="checkbox"
                                  checked={selectedPermissions.includes(permission)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedPermissions([...selectedPermissions, permission]);
                                    } else {
                                      setSelectedPermissions(selectedPermissions.filter(p => p !== permission));
                                    }
                                  }}
                                  className="mt-1 mr-3 text-primary-600 dark:text-primary-400"
                                />
                                <div>
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {permission.replace('_', ' ').toLowerCase()}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {descriptions[permission]}
                                  </div>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Expiration */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Invitation Expires In
                    </label>
                    <select
                      value={expirationDays}
                      onChange={(e) => setExpirationDays(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white/90 dark:bg-gray-800/90 text-gray-900 dark:text-white"
                    >
                      <option value={1}>1 day</option>
                      <option value={3}>3 days</option>
                      <option value={7}>7 days</option>
                      <option value={14}>14 days</option>
                      <option value={30}>30 days</option>
                    </select>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => setShowCreateInvitation(false)}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateInvitation}
                      disabled={createInvitationLoading || selectedPermissions.length === 0}
                      className="px-4 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {createInvitationLoading ? 'Creating...' : 'Create Invitation'}
                    </button>
                  </div>
                </div>
              ) : (
                /* Invitation Created */
                <div className="text-center space-y-6">
                  <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Invitation Created!</h3>
                    <p className="text-gray-600 dark:text-gray-300">Share this QR code or link with your worker to get them started.</p>
                  </div>

                  {/* QR Code */}
                  {qrCodeDataUrl && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
                      <img src={qrCodeDataUrl} alt="Invitation QR Code" className="mx-auto mb-4" />
                      <button
                        onClick={downloadQRCode}
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 flex items-center mx-auto transition-colors duration-200"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download QR Code
                      </button>
                    </div>
                  )}

                  {/* Invitation Link */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Invitation Link
                    </label>
                    <div className="flex">
                      <input
                        type="text"
                        value={invitationUrl}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md bg-white/90 dark:bg-gray-800/90 text-sm text-gray-900 dark:text-white"
                      />
                      <button
                        onClick={() => copyToClipboard(invitationUrl)}
                        className="px-3 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded-r-md hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors duration-200"
                      >
                        {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <button
                      onClick={() => {
                        setShowCreateInvitation(false);
                        setInvitationUrl('');
                        setQrCodeDataUrl('');
                      }}
                      className="px-6 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors duration-200"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      , document.body)}

      {/* Edit User Modal */}
      {showEditUser && editingUser && createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 animate-fade-in" style={{ zIndex: 999999 }}>
          <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-lg max-w-2xl w-full h-full md:h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700 shadow-2xl">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Edit User Permissions</h2>
                <button
                  onClick={handleCancelEdit}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* User Info */}
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">User Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Name:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">
                      {editingUser.firstName} {editingUser.lastName}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Email:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{editingUser.email}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Role:</span>
                    <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      editingUser.role === 'owner' 
                        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300' 
                        : 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300'
                    }`}>
                      {editingUser.role}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Joined:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">
                      {formatDate(editingUser.createdAt)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Permissions */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Manage Permissions</h3>
                <div className="space-y-4">
                  {Object.entries(permissionCategories).map(([category, { permissions, descriptions }]) => (
                    <div key={category} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50/50 dark:bg-gray-700/30">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-3">{category}</h4>
                      <div className="space-y-2">
                        {permissions.map((permission) => (
                          <label key={permission} className="flex items-start">
                            <input
                              type="checkbox"
                              checked={editUserPermissions.includes(permission)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setEditUserPermissions([...editUserPermissions, permission]);
                                } else {
                                  setEditUserPermissions(editUserPermissions.filter(p => p !== permission));
                                }
                              }}
                              className="mt-1 mr-3 text-primary-600 dark:text-primary-400"
                            />
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {permission.replace('_', ' ').toLowerCase()}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {descriptions[permission]}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700 mt-6">
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUserEdit}
                  disabled={editUserLoading}
                  className="px-4 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {editUserLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Delete User Confirmation Modal */}
      {isDeleteModalOpen && deletingUser && createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 animate-fade-in" style={{ zIndex: 999999 }}>
          <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-lg max-w-md w-full border border-gray-200 dark:border-gray-700 shadow-2xl">
            <div className="p-4 sm:p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-4">
                  <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Delete User</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">This action cannot be undone</p>
                </div>
              </div>

              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                <p className="text-gray-900 dark:text-white mb-2">
                  Are you sure you want to delete <span className="font-semibold">{deletingUser.firstName} {deletingUser.lastName}</span>?
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This will remove them from your business and revoke all their access permissions.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelDelete}
                  disabled={deleteUserLoading}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={deleteUserLoading}
                  className="px-4 py-2 bg-red-600 dark:bg-red-500 text-white rounded-lg hover:bg-red-700 dark:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {deleteUserLoading ? 'Deleting...' : 'Delete User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
};

export default UserManagement; 